import type { EnterpriseNavItem } from '../../dashboard/components/EnterpriseGroupedNav/enterpriseNavGroups.js';

/**
 * Tenant dashboard compliance nav that needs a numeric tenant id.
 * Consents & GDPR are platform-only (`/dev/compliance`) — not listed here.
 */
export const TENANT_SCOPED_COMPLIANCE_NAV_IDS = new Set<string>(['audit-logs']);

export function isTenantScopedComplianceNavItem(item: Pick<EnterpriseNavItem, 'id'>): boolean {
  return TENANT_SCOPED_COMPLIANCE_NAV_IDS.has(item.id);
}

/** True for dashboard section keys that require a resolvable compliance tenant id. */
export function isTenantScopedComplianceSection(section: string): boolean {
  return TENANT_SCOPED_COMPLIANCE_NAV_IDS.has(section);
}

export interface ResolveAdminComplianceTenantIdInput {
  readonly userTenantId?: number | null;
  readonly currentTenantId?: number | null;
  readonly tenantScope?: readonly number[] | '*';
}

/**
 * Resolves the tenant id for tenant-scoped compliance screens and nav.
 * Order: active session currentTenantId, then user.tenantId FK, then a single-entry
 * tenantScope. Preferring currentTenantId avoids silent wrong-tenant data when a
 * multi-tenant admin has switched away from their home tenant.
 *
 * Option B (platform-* honesty): tenantScope '*' / multi-tenant without a bound
 * numeric tenant ⇒ undefined. Compliance nav stays hidden until TenantSwitcher
 * (or single-scope / user FK) binds a tenant. No commercial URL→tenantId SoT.
 */
export function resolveAdminComplianceTenantId(
  input: ResolveAdminComplianceTenantIdInput,
): number | undefined {
  if (input.currentTenantId != null) {
    return input.currentTenantId;
  }
  if (input.userTenantId != null) {
    return input.userTenantId;
  }
  if (Array.isArray(input.tenantScope) && input.tenantScope.length === 1) {
    const only = input.tenantScope[0];
    return only ?? undefined;
  }
  return undefined;
}
